import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { subscribeNewsletter } from "@/lib/api";

const STORAGE_KEY = "studio-a-newsletter-v1";

export default function NewsletterModal() {
  const [open, setOpen] = useState(false);
  const [first, setFirst] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [already, setAlready] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const seen = localStorage.getItem(STORAGE_KEY);
    if (seen) return;
    const t = setTimeout(() => setOpen(true), 6000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || status === "loading") return;
    setStatus("loading");
    try {
      const res = await subscribeNewsletter(email, first);
      setAlready(!!res.already);
      setStatus("done");
      setTimeout(close, 3200);
    } catch { setStatus("error"); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="newsletter-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed inset-0 z-[9999] bg-[#1B1A18]/40 backdrop-blur-sm flex items-center justify-center px-6"
          onClick={close}
        >
          <motion.div
            initial={{ opacity: 0, y: 22, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 22 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-[540px] bg-[#F7F6F2] p-10 sm:p-14"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={close}
              aria-label="Close"
              data-testid="newsletter-close"
              className="absolute top-5 right-5 h-8 w-8 grid place-items-center text-[#55524C] hover:text-[#2E2B27]"
            >
              <X size={16} strokeWidth={1.4} />
            </button>

            <div className="label-eyebrow">Studio A · Bondi</div>
            <h2 className="serif mt-6 text-[36px] sm:text-[44px] leading-[1.05] tracking-tight">Welcome to Studio A</h2>
            <p className="mt-6 text-[15px] leading-[1.8] text-[#55524C] max-w-md">
              Join our community and receive 10% off your first facial treatment, along with occasional skincare advice, seasonal offers and wellness inspiration.
            </p>

            {status !== "done" ? (
              <form onSubmit={submit} className="mt-10 space-y-5" data-testid="newsletter-modal-form">
                <div>
                  <label className="label-eyebrow" htmlFor="nm-first">First name</label>
                  <input id="nm-first" required minLength={2} data-testid="newsletter-modal-first"
                    className="input-soft" value={first} onChange={(e) => setFirst(e.target.value)} />
                </div>
                <div>
                  <label className="label-eyebrow" htmlFor="nm-email">Email address</label>
                  <input id="nm-email" type="email" required data-testid="newsletter-modal-email"
                    className="input-soft" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <button type="submit" data-testid="newsletter-modal-submit"
                  disabled={status === "loading"} className="btn-primary w-full justify-center mt-4">
                  {status === "loading" ? "Sending…" : "Join & Receive 10% Off"}
                </button>
                {status === "error" && (
                  <p className="text-[13px] text-center" style={{ color: "var(--terracotta)" }}>Please check the details and try again.</p>
                )}
                <p className="text-[12px] text-[#6E6A63] italic pt-2 text-center">No spam. Only thoughtful emails.</p>
              </form>
            ) : (
              <div className="mt-10 border-t border-[#D5CDBF] pt-8" data-testid="newsletter-modal-done">
                <p className="serif italic text-[22px] text-[#2E2B27]">Thank you, {first || "and welcome"}.</p>
                <p className="mt-3 text-[14px] text-[#55524C]">
                  {already
                    ? "You're already on our list — your 10% off code was sent when you first joined. Can't find it? Write to anissa@studioanissa.com.au."
                    : "Your 10% off code is on its way to your inbox."}
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
